import React, { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';

/**
 * Responsive Logo Component
 * Automatically adjusts size based on screen width and configuration
 * 
 * @param {string} variant - 'default' | 'white' | 'email' | 'pdf'
 * @param {string} className - Additional CSS classes
 * @param {string} alt - Alt text (defaults to company name)
 * @param {boolean} inverted - Apply brightness invert for dark backgrounds
 * @param {number} heightOverride - Override the configured height (pixels)
 */
export default function Logo({ 
  variant = 'default', 
  className = '', 
  alt,
  inverted = false,
  heightOverride = null
}) {
  const { config, getLogoUrl, getLogoHeight } = useConfig();
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const logoUrl = getLogoUrl(variant);
  const height = heightOverride || getLogoHeight(screenWidth);
  const altText = alt || config?.company_name || 'Mitonner';

  // Generate responsive style with CSS variables from config
  const style = {
    height: `${height}px`,
    width: 'auto',
    objectFit: 'contain'
  };

  return (
    <img 
      src={logoUrl} 
      alt={altText}
      style={style}
      className={`${inverted ? 'brightness-0 invert' : ''} ${className}`}
      data-testid="logo"
    />
  );
}

/**
 * Logo with responsive CSS classes (alternative approach)
 * Uses Tailwind breakpoints instead of JS resize listener
 */
export function LogoResponsive({ 
  variant = 'default', 
  className = '', 
  alt,
  inverted = false
}) {
  const { config, getLogoUrl } = useConfig();
  
  const logoUrl = getLogoUrl(variant);
  const altText = alt || config?.company_name || 'Mitonner';
  
  // Use CSS custom properties for dynamic sizing
  const desktopHeight = config?.logo_height_desktop || 48;
  const tabletHeight = config?.logo_height_tablet || 40;
  const mobileHeight = config?.logo_height_mobile || 36;

  return (
    <img 
      src={logoUrl} 
      alt={altText}
      className={`
        object-contain
        ${inverted ? 'brightness-0 invert' : ''}
        ${className}
      `}
      style={{
        '--logo-h-mobile': `${mobileHeight}px`,
        '--logo-h-tablet': `${tabletHeight}px`,
        '--logo-h-desktop': `${desktopHeight}px`,
        height: `var(--logo-h-mobile)`,
      }}
      data-testid="logo-responsive"
    />
  );
}
